import { ConfigProvider } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { Col, Descriptions, Row, Space, Typography } from "antd";
import { Slider } from "antd";
import LPCustomNumericFormat from "../../../../LongpollControls/LPCustomNumericFormat";
import type { ApplicationSummaryBlockProps } from "../../types";

const PaymentTerms = React.memo(({ application, onChange, canEditApplicationFields, onCommit }: ApplicationSummaryBlockProps) => {
  const { t } = useTranslation("translations");
  return (
    <ConfigProvider
      theme={{
        token: { fontSize: 12 },
        components: {
          Select: {
            activeBorderColor: "transparent",
            activeOutlineColor: "transparent",
          },
          Input: {
            activeBorderColor: "transparent",
            activeShadow: "none",
            inputFontSize: 12,
            inputFontSizeSM: 12,
            inputFontSizeLG: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            paddingBlockLG: 0,
            paddingInlineLG: 0,
          },
          InputNumber: {
            activeBorderColor: "transparent",
            activeShadow: "none",
            inputFontSize: 12,
            inputFontSizeSM: 12,
            inputFontSizeLG: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            paddingBlockLG: 0,
            paddingInlineLG: 0,
          },
        },
      }}
      select={{ styles: { root: { padding: 0 } } }}
      textArea={{ styles: { textarea: { minHeight: 22 } } }}
      descriptions={{
        styles: {
          label: { padding: "6px 8px" },
          content: { padding: "6px 8px" },
        },
      }}
    >
      <Descriptions
        bordered
        size="small"
        items={[
          {
            label: t("payment_terms"),
            span: 3,

            styles: { label: { width: 250 } },
            children: canEditApplicationFields ? (
              <Row gutter={8} align="middle">
                <Col span={4}>
                  {t("prepayment")}
                  <LPCustomNumericFormat
                    variant="borderless"
                    decimalScale={0}
                    allowNegative={false}
                    isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 0 && floatValue <= 100)}
                    style={{ width: "100%" }}
                    value={application.prepaymentPercent}
                    onChange={(value) => {
                      if (value === undefined) {
                        onChange({ ...application, prepaymentPercent: undefined, postpaymentPercent: undefined });
                      } else {
                        onChange({ ...application, prepaymentPercent: value, postpaymentPercent: 100 - value });
                      }
                      onCommit?.();
                    }}
                  />
                </Col>
                <Col span={16}>
                  <Slider
                    min={0}
                    max={100}
                    marks={{ 10: "10%", 20: "20%", 30: "30%", 40: "40%", 50: "50%", 60: "60%", 70: "70%", 80: "80%", 90: "90%" }}
                    tooltip={{ formatter: (v) => `${v}%` }}
                    value={application.prepaymentPercent}
                    onChange={(value) => {
                      onChange({ ...application, prepaymentPercent: value, postpaymentPercent: 100 - value });
                      onCommit?.();
                    }}
                  />
                </Col>
                <Col span={4}>
                  {t("postpayment")}
                  <LPCustomNumericFormat
                    variant="borderless"
                    decimalScale={0}
                    allowNegative={false}
                    isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 0 && floatValue <= 100)}
                    style={{ width: "100%" }}
                    value={application.postpaymentPercent}
                    onChange={(value) => {
                      if (value === undefined) {
                        onChange({ ...application, prepaymentPercent: undefined, postpaymentPercent: undefined });
                      } else {
                        onChange({ ...application, prepaymentPercent: 100 - value, postpaymentPercent: value });
                      }
                      onCommit?.();
                    }}
                  />
                </Col>
              </Row>
            ) : (
              <Space>
                <Typography.Text strong>{t("prepayment")}:</Typography.Text>
                <Typography.Text strong>{application.prepaymentPercent ? `${application.prepaymentPercent}%` : "-"}</Typography.Text>
                <Typography.Text strong>{t("postpayment")}:</Typography.Text>
                <Typography.Text strong>{application.postpaymentPercent ? `${application.postpaymentPercent}%` : "-"}</Typography.Text>
              </Space>
            ),
          },
        ]
          .flat(3)
          .filter((item) => !!item)}
      />
    </ConfigProvider>
  );
});
export default PaymentTerms;
